import { useRef, useState, type CSSProperties } from 'react';
import { FILES, type HouseRuleSpec, type ItemSpec } from '../../../engine';
import {
  highTableHeadStart,
  LONG_NIGHT,
  interestOn,
  reorderMantel,
  skipTable,
  type FieldedPawn,
  type NightRun,
  type NightTable,
  type Profile,
} from '../../../campaign';
import { t } from '../../../i18n';
import { itemName, itemText } from '../../../app/describe';
import { sounds } from '../../../app/sound';
import { REGULAR_LAMP } from '../../scene';
import { formatScore } from '../ScoreBurst';
import { Modal } from '../Modal';
import { PawnFigure } from '../PawnFigure';
import { ruleText } from '../LedgerStrip';
import { BenchDialog } from '../NightCounter';
import { BrokerEyes, BrokerHand } from './Broker';
import { BrassHud, NightLabel } from './Hud';
import { InventorySheet, pawnLabel } from './Inventory';
import { ItemSheet, type ShowItem } from './Item';
import { NightObject } from './Objects';
import { Emblem } from './Emblem';
import { info, nightTitle, opponentName, opponentPortrait, roman, tableKindName } from './runText';

/**
 * The Night board (docs/LONG_NIGHT_ART_DIRECTION.md §11.4–§11.5): the Night's tables as a short
 * numbered list in the order they are played, the next one lit in its patron's light, the ones
 * behind you marked with how they went. Each row says who sits there and the head start you must
 * beat; its House Rules are chips that explain themselves, and the next table says what skipping
 * it would earn. A tap on a row explains the table in full.
 */

type Apply = (fn: (p: Profile) => Profile) => void;

/** How much each skipped table raises the High Table's head start, in percent. */
const SKIP_PCT = Math.round(LONG_NIGHT.SKIP_RAISE * 100);

/**
 * What skipping the current table does to tonight's High Table, in numbers (issue #10): its head
 * start now and after the skip, the Toll Waiver's halving included when it will apply.
 */
function skipResult(run: NightRun): string {
  const now = highTableHeadStart(run);
  const then = highTableHeadStart(run, true);
  if (now === null || then === null) return '';
  const waiver =
    run.favors.includes('toll-waiver') || run.board[run.table]?.favor === 'toll-waiver';
  return t(waiver ? 'night.skipResultWaiver' : 'night.skipResult', {
    now: formatScore(now),
    then: formatScore(then),
    pct: SKIP_PCT,
  });
}
const skippedTonight = (run: NightRun) => run.board.filter((t) => t.status === 'skipped').length;

export function BoardScene({
  profile,
  run,
  apply,
  reduceMotion,
  onPlay,
  onAbandon,
}: {
  profile: Profile;
  run: NightRun;
  apply: Apply;
  reduceMotion: boolean;
  onPlay: (fielding: FieldedPawn[] | null) => void;
  onAbandon: () => void;
}) {
  const [files, setFiles] = useState<string[]>(() => {
    const valid = run.fielding.filter((id) => run.roster.some((r) => r.id === id));
    return valid.length === 6 ? [...run.fielding] : run.roster.slice(0, 6).map((r) => r.id);
  });
  const [sheet, setSheet] = useState<ShowItem | null>(null);
  const [details, setDetails] = useState<number | null>(null);
  const [fielding, setFielding] = useState(false);
  const [inventory, setInventory] = useState(false);
  const [bench, setBench] = useState<ItemSpec | null>(null);
  const [skip, setSkip] = useState<'offer' | 'snuff' | null>(null);
  const table = run.board[run.table];
  if (!table) return null;
  const used = new Set(files.filter(Boolean));
  const complete = files.every(Boolean) && used.size === 6;
  const changed = files.some((id, i) => run.fielding[i] !== id);
  const oneRound = table.kind === 'small' || table.kind === 'big';
  const closing = run.night === LONG_NIGHT.NIGHTS + 1;

  return (
    <div className="ln-screen nb has-dock">
      <header className="nb-head">
        <NightLabel night={run.night}>
          {itemName(`pledge.${run.pledge}`)} · {t('night.termN', { n: roman(run.term) })}
        </NightLabel>
        <BrassHud
          brass={run.brass}
          interest={interestOn(run)}
          reduceMotion={reduceMotion}
          onClick={() => setInventory(true)}
        />
      </header>
      {/* At Closing Time his eyes have the top of the board to themselves, over the smoke. */}
      {closing && (
        <div className="nb-gaze">
          <BrokerEyes open still={reduceMotion} />
        </div>
      )}
      <h2 className="nb-title">{nightTitle(run.night)}</h2>

      {/* The Night's tables in the order they are played, the next one lit. */}
      <ol className="nb-list">
        {run.board.map((tb, i) => (
          <TableRow
            key={i}
            n={i + 1}
            table={tb}
            next={i === run.table}
            raised={tb.kind === 'high' ? skippedTonight(run) : 0}
            skipNumbers={
              i === run.table
                ? { now: highTableHeadStart(run), then: highTableHeadStart(run, true) }
                : null
            }
            onOpen={() => setDetails(i)}
            onRule={(r) =>
              setSheet(
                info(
                  t('night.kind.rule'),
                  itemName(`rule.${r.id}`),
                  `${ruleText(r)}\n\n${t('night.rule.how')}`,
                  'ledger',
                ),
              )
            }
          />
        ))}
      </ol>

      {/* Closing Time: his House Rule, a contract in ink, signed; his eyes above the lamp. */}
      {closing && <Contract run={run} still={reduceMotion} />}

      {/* Your six, on your home row. */}
      <button type="button" className="nb-lineup" onClick={() => setFielding(true)}>
        <span className="nb-lineup-cap">
          <span>{t('night.lineup')}</span>
          <span>{t('night.change')}</span>
        </span>
        {files.map((id, i) => (
          <span key={i} className="nb-file">
            <i aria-hidden="true" />
            <span>{id ? pawnLabel(profile, run, id).split(' ')[0] : '—'}</span>
          </span>
        ))}
      </button>

      {skip === 'offer' && table.favor && (
        <div className="nb-skip">
          <BrokerHand
            pose="reach"
            at={0.5}
            to={0.42}
            still={reduceMotion}
            holding={
              <NightObject spec={{ kind: 'favor', emblem: `favor.${table.favor}` }} size={70} />
            }
          />
        </div>
      )}

      <div className="ln-dock">
        {oneRound && table.favor && (
          <button type="button" className="btn btn-large" onClick={() => setSkip('offer')}>
            {t('night.skipShort')}
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary btn-large ln-dock-main"
          disabled={!complete}
          onClick={() => onPlay(changed ? files.map((id, file) => ({ id, file })) : null)}
        >
          {t('night.sitDown')}
        </button>
      </div>

      {details !== null && run.board[details] && (
        <TableSheet run={run} table={run.board[details]!} onClose={() => setDetails(null)} />
      )}
      {sheet && <ItemSheet item={sheet} actions={[]} onClose={() => setSheet(null)} />}
      {skip === 'offer' && table.favor && (
        <ItemSheet
          item={info(
            t('night.kind.favor'),
            itemName(`favor.${table.favor}`),
            [
              itemText(`favor.${table.favor}`),
              t('night.skipText', { pct: SKIP_PCT }),
              skipResult(run),
            ]
              .filter(Boolean)
              .join('\n\n'),
            'favor',
            { kind: 'favor', emblem: `favor.${table.favor}` },
          )}
          actions={[
            {
              label: t('night.skip', { favor: itemName(`favor.${table.favor}`) }),
              primary: true,
              onClick: () => {
                // The table's pool is snuffed; the note's stub stays on the board.
                sounds.play('snuff');
                sounds.play('paper');
                setSkip('snuff');
                setTimeout(
                  () => {
                    setSkip(null);
                    apply(skipTable);
                  },
                  reduceMotion ? 0 : 700,
                );
              },
            },
          ]}
          onClose={() => setSkip(null)}
        />
      )}
      {fielding && (
        <Fielding
          profile={profile}
          run={run}
          files={files}
          onDone={(next) => {
            setFiles(next);
            setFielding(false);
          }}
          onClose={() => setFielding(false)}
        />
      )}
      {inventory && (
        <InventorySheet
          profile={profile}
          run={run}
          onReorder={(uids) => apply((p) => reorderMantel(p, uids))}
          onBench={(c) => {
            setInventory(false);
            setBench(c);
          }}
          onAbandon={() => {
            setInventory(false);
            onAbandon();
          }}
          onClose={() => setInventory(false)}
        />
      )}
      {bench && <BenchDialog run={run} item={bench} apply={apply} onClose={() => setBench(null)} />}
      {skip === 'snuff' && <span className="nb-snuffing" aria-hidden="true" />}
    </div>
  );
}

/**
 * His House Rule at Closing Time (§11.17), as a contract in ink, signed. The Final Appraisal
 * shows the receipt of your best chain, writes "× 2" beside it, and the Ante changes.
 */
function Contract({ run, still }: { run: NightRun; still: boolean }) {
  const rule = run.brokerRule;
  const ante = run.board[0]?.ante ?? 0;
  const appraised = Math.max(ante, 2 * run.bestChain);
  return (
    <div className={`nb-contract ${still ? 'is-still' : ''}`}>
      <p className="nb-contract-kind">{t('night.contract')}</p>
      <h3 className="nb-contract-name">{itemName(`broker.${rule}`)}</h3>
      <p className="nb-contract-text">{itemText(`broker.${rule}`)}</p>
      {rule === 'final-appraisal' && run.bestChain > 0 && (
        <p className="nb-appraisal">
          <span>{t('night.contract.best', { n: formatScore(run.bestChain) })}</span>
          <i className="nb-x2">× 2</i>
          {appraised !== ante && (
            <span className="nb-ante">
              {t('night.contract.ante')} <s>{formatScore(ante)}</s> {formatScore(appraised)}
            </span>
          )}
        </p>
      )}
      {/* And his power (night/Horrors.tsx): he blinds one of your eyes. */}
      <p className="nb-contract-also">{t('night.contract.blindEye')}</p>
      <svg className="nb-sign" viewBox="0 0 120 30" aria-hidden="true">
        <path d="M4 22 C12 4 20 30 30 14 S44 6 50 20 S66 26 72 12 S90 6 96 20 C100 26 108 22 116 16" />
      </svg>
    </div>
  );
}

/**
 * One table of the Night, on one row: its number in the order of play, who sits there, the head
 * start, its rules, and what skipping it earns. The next table is lit; the others wait in the
 * dark or show how they went. A tap explains it in full.
 */
function TableRow({
  n,
  table,
  next,
  raised,
  skipNumbers,
  onOpen,
  onRule,
}: {
  n: number;
  table: NightTable;
  next: boolean;
  /** Tables skipped tonight, each of which raised the High Table's head start. */
  raised: number;
  /** The next table: tonight's High Table head start now, and if this one is skipped. */
  skipNumbers: { now: number | null; then: number | null } | null;
  onOpen: () => void;
  onRule: (r: HouseRuleSpec) => void;
}) {
  const tint =
    table.kind === 'high' && table.regular !== undefined ? REGULAR_LAMP[table.regular] : undefined;
  const done = table.status !== 'open';
  const rules = table.kind === 'closing' ? [] : table.rules;
  const fullMatch = table.kind === 'high' || table.kind === 'closing';
  return (
    <li
      className={`nb-item status-${table.status} ${next ? 'is-next' : ''}`}
      style={tint ? ({ '--pool': tint } as CSSProperties) : undefined}
    >
      <button
        type="button"
        className="nb-item-main"
        onClick={onOpen}
        aria-label={`${n}. ${tableKindName(table.kind)} · ${opponentName(table)}`}
      >
        <span className="nb-portrait" aria-hidden="true">
          <Emblem name={opponentPortrait(table)} size={44} />
          {table.status !== 'open' && <TableMark status={table.status} />}
        </span>
        <span className="nb-item-text">
          <span className="nb-kind">{tableKindName(table.kind)}</span>
          <span className="nb-opp">{opponentName(table)}</span>
          <span className="nb-facts">
            {table.ante > 0 ? (
              <>
                {t('night.headStart')} <b>{formatScore(table.ante)}</b>
                {fullMatch && <> {t('night.perRound')}</>}
                {raised > 0 && (
                  <span className="nb-raised"> ▲ {t('night.raisedBy', { n: raised })}</span>
                )}
              </>
            ) : (
              t('night.evenStart')
            )}
            {table.omens.length > 0 && <> · {t('night.omensCount', { n: table.omens.length })}</>}
          </span>
        </span>
        <span className="nb-state">
          {next ? t('night.nextTable') : done ? t(`night.status.${table.status}`) : ''}
        </span>
      </button>
      {(rules.length > 0 || (next && table.favor && table.status === 'open')) && (
        <span className="nb-extras">
          {rules.slice(0, 2).map((r, i) => (
            <button key={i} type="button" className="nb-rule" onClick={() => onRule(r)}>
              {itemName(`rule.${r.id}`)}
            </button>
          ))}
          {next && table.favor && table.status === 'open' && (
            <span className="nb-favor">
              {skipNumbers && skipNumbers.now !== null && skipNumbers.then !== null
                ? t('night.skipForN', {
                    favor: itemName(`favor.${table.favor}`),
                    now: formatScore(skipNumbers.now),
                    then: formatScore(skipNumbers.then),
                  })
                : t('night.skipFor', {
                    favor: itemName(`favor.${table.favor}`),
                    pct: SKIP_PCT,
                  })}
            </span>
          )}
        </span>
      )}
    </li>
  );
}

/** How a table went, stamped on its portrait's corner in pixels: a check, a cross, a slash. */
function TableMark({ status }: { status: Exclude<NightTable['status'], 'open'> }) {
  const d =
    status === 'won'
      ? 'M6 0h1v1H6zM5 1h1v1H5zM6 1h1v1H6zM4 2h1v1H4zM5 2h1v1H5zM0 3h1v1H0zM3 3h1v1H3zM4 3h1v1H4zM0 4h1v1H0zM1 4h1v1H1zM2 4h1v1H2zM3 4h1v1H3zM1 5h1v1H1zM2 5h1v1H2z'
      : status === 'lost'
        ? 'M0 0h1v1H0zM1 0h1v1H1zM5 0h1v1H5zM6 0h1v1H6zM1 1h1v1H1zM2 1h1v1H2zM4 1h1v1H4zM5 1h1v1H5zM2 2h1v1H2zM3 2h1v1H3zM4 2h1v1H4zM2 3h1v1H2zM3 3h1v1H3zM4 3h1v1H4zM1 4h1v1H1zM2 4h1v1H2zM4 4h1v1H4zM5 4h1v1H5zM0 5h1v1H0zM1 5h1v1H1zM5 5h1v1H5zM6 5h1v1H6z'
        : 'M1 2h1v1H1zM2 2h1v1H2zM3 2h1v1H3zM4 2h1v1H4zM5 2h1v1H5zM1 3h1v1H1zM2 3h1v1H2zM3 3h1v1H3zM4 3h1v1H4zM5 3h1v1H5z';
  return (
    <svg className={`nb-mark is-${status}`} viewBox="-1 -1 9 8" shapeRendering="crispEdges">
      <path d={d} />
    </svg>
  );
}

/** Everything about one table, explained. */
function TableSheet({
  run,
  table,
  onClose,
}: {
  run: NightRun;
  table: NightTable;
  onClose: () => void;
}) {
  const lamp = run.fixtures.includes('oil-lamp');
  const omens = table.omens.map((o) => (o.shrouded && !lamp ? 'unknown' : o.kind));
  const lines = [
    table.ante > 0
      ? `${t('night.anteValue', { n: formatScore(table.ante) })}. ${t('night.headStartText')}`
      : t('night.evenStart'),
    table.kind === 'high' && skippedTonight(run) > 0
      ? t('night.raisedText', { n: skippedTonight(run), pct: SKIP_PCT })
      : '',
    t(
      table.kind === 'small' || table.kind === 'big' ? 'night.oneRoundText' : 'night.fullMatchText',
    ),
    table.patron && run.term >= 2 ? itemText(`patron.${table.patron}`) : '',
    ...table.rules.map((r) => `${itemName(`rule.${r.id}`)}: ${ruleText(r)}`),
    table.kind === 'closing'
      ? `${itemName(`broker.${run.brokerRule}`)}: ${itemText(`broker.${run.brokerRule}`)}`
      : '',
    omens.length > 0
      ? [
          t('night.omensText'),
          ...[...new Set(omens)].map((k) =>
            k === 'unknown'
              ? `${t('night.shrouded')}: ${itemText('omen.unknown')}`
              : `${itemName(`omen.${k}`)}: ${itemText(`omen.${k}`)}`,
          ),
        ].join('\n')
      : '',
    table.favor && table.status === 'open'
      ? `${t('night.favorShort')}: ${itemName(`favor.${table.favor}`)}. ${itemText(`favor.${table.favor}`)}`
      : '',
  ].filter(Boolean);
  return (
    <ItemSheet
      item={info(tableKindName(table.kind), opponentName(table), lines.join('\n\n'), 'info', {
        kind: 'emblem',
        emblem: opponentPortrait(table),
      })}
      actions={[]}
      onClose={onClose}
    />
  );
}

/**
 * Fielding (§11.3): your pawns on a shelf with paper name tags, and below them your home row,
 * its six squares lit. Tap a pawn, then a square, to field it; tap a fielded pawn to send it
 * back to the shelf. Holding a pawn turns its tag over to show its record. Empty squares are
 * filled by stand-ins on their own. "Fill in order" stays, as a quiet button.
 */
function Fielding({
  profile,
  run,
  files: initial,
  onDone,
  onClose,
}: {
  profile: Profile;
  run: NightRun;
  files: readonly string[];
  onDone: (files: string[]) => void;
  onClose: () => void;
}) {
  const [files, setFiles] = useState<string[]>([...initial]);
  const [held, setHeld] = useState<string | null>(null);
  const [turned, setTurned] = useState<string | null>(null);
  const hold = useRef<ReturnType<typeof setTimeout> | null>(null);
  const placed = new Set(files.filter(Boolean));
  const shelf = run.roster.filter((r) => !placed.has(r.id));
  const named = (id: string) => !run.roster.find((r) => r.id === id)?.standIn;
  const place = (file: number) => {
    if (held) {
      setFiles((cur) => cur.map((x, i) => (i === file ? held : x === held ? '' : x)));
      setHeld(null);
      sounds.play('place');
    } else if (files[file]) {
      // A fielded pawn goes back to the shelf.
      setFiles((cur) => cur.map((x, i) => (i === file ? '' : x)));
    }
  };
  const press = (id: string) => {
    hold.current = setTimeout(() => {
      setTurned(id);
      hold.current = null;
    }, 450);
  };
  const release = (id: string) => {
    if (hold.current) {
      clearTimeout(hold.current);
      hold.current = null;
      setHeld((h) => (h === id ? null : id));
    }
    setTurned(null);
  };
  const finish = () => {
    // Stand-ins fill the empty squares on their own.
    const spare = run.roster.filter((r) => !files.includes(r.id)).map((r) => r.id);
    onDone(files.map((x) => x || spare.shift() || ''));
  };
  /** A pawn: on the shelf it is picked up; on a square it goes back, or swaps with the held one. */
  const pawn = (id: string, file?: number) => {
    const records = profile.pawns[id]?.records;
    const carvings = run.carvings[id] ?? [];
    return (
      <button
        key={id}
        type="button"
        className={`fl-pawn ${held === id ? 'is-held' : ''} ${named(id) ? '' : 'is-stand-in'}`}
        onPointerDown={() => press(id)}
        onPointerUp={() => {
          if (file !== undefined && held && held !== id && !turned) {
            if (hold.current) clearTimeout(hold.current);
            hold.current = null;
            place(file);
            return;
          }
          if (file !== undefined && !held && hold.current) {
            clearTimeout(hold.current);
            hold.current = null;
            place(file);
            return;
          }
          release(id);
        }}
        onPointerLeave={() => {
          if (hold.current) clearTimeout(hold.current);
          hold.current = null;
          setTurned(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setHeld((h) => (h === id ? null : id));
          }
        }}
        aria-pressed={held === id}
      >
        <span className="fl-figure">
          <PawnFigure side="S" crowned={false} immune={false} inscription={null} />
          {carvings.length > 0 && <span className="pawn-carved" aria-hidden="true" />}
        </span>
        {named(id) && (
          <span className={`fl-tag ${turned === id ? 'is-turned' : ''}`}>
            {turned === id && records ? (
              <small>
                {t('ledgerBook.record', {
                  tables: records.tablesPlayed,
                  captures: records.captures,
                  best: formatScore(records.bestChain),
                })}
              </small>
            ) : (
              <>
                {/* On the home row, the first name; on the shelf, the whole name. */}
                {file === undefined
                  ? pawnLabel(profile, run, id)
                  : pawnLabel(profile, run, id).split(' ')[0]}
                {carvings.length > 0 && (
                  <small>{carvings.map((c) => itemName(c.id)).join(', ')}</small>
                )}
              </>
            )}
          </span>
        )}
      </button>
    );
  };
  return (
    <Modal
      title={t('night.lineup')}
      onClose={onClose}
      wide
      className="ln-sheet fielding"
      footer={
        <>
          <button
            type="button"
            className="btn"
            onClick={() => setFiles(run.roster.slice(0, 6).map((r) => r.id))}
          >
            {t('campaign.fieldAuto')}
          </button>
          <button type="button" className="btn btn-primary" onClick={finish}>
            {t('action.confirm')}
          </button>
        </>
      }
    >
      <p className="ln-hint">{t('night.fieldingHint')}</p>
      <div className="fl-shelf">{shelf.length > 0 ? shelf.map((r) => pawn(r.id)) : null}</div>
      <ol className="fl-row">
        {FILES.split('').map((f, i) => (
          <li key={f} className={`fl-square ${held ? 'is-target' : ''}`}>
            {files[i] ? (
              pawn(files[i]!, i)
            ) : (
              <button
                type="button"
                className="fl-empty"
                onClick={() => place(i)}
                aria-label={t('campaign.file', { file: f })}
              />
            )}
            <span className="fl-file">{f}</span>
          </li>
        ))}
      </ol>
    </Modal>
  );
}
